/**
 * GraphOverlayLegend — floating legend for the active graph overlay
 * (Betweenness, PageRank, Communities, Risk).
 *
 * Default placement is upper-left so it stays clear of the bottom zoom /
 * rearrange chrome. Draggable from the header and resizable from all eight
 * edges/corners — same interaction model as GraphLegend.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Activity, Users, TrendingUp, ShieldAlert, GripHorizontal, RotateCcw } from 'lucide-react';
import { clsx } from 'clsx';
import { workbenchSurface } from './workbenchStyles';
import type { OverlayType, OverlayLegendItem } from '../../hooks/useGraphOverlay';

interface GraphOverlayLegendProps {
    activeOverlay: OverlayType;
    legend: OverlayLegendItem[];
    isComputing: boolean;
    onClear: () => void;
    className?: string;
}

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface PanelRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

/** Upper-left canvas placement — clear of bottom toolbars and left zoom strip. */
const OVERLAY_DEFAULT: PanelRect = {
    top: 16,
    left: 56,
    width: 220,
    height: 200,
};

const MIN_WIDTH = 180;
const MIN_HEIGHT = 140;

const RESIZE_HANDLES: Array<{ edge: ResizeEdge; className: string; cursor: string }> = [
    { edge: 'n', className: 'top-0 left-2 right-2 h-1.5 -translate-y-1/2', cursor: 'ns-resize' },
    { edge: 's', className: 'bottom-0 left-2 right-2 h-1.5 translate-y-1/2', cursor: 'ns-resize' },
    { edge: 'e', className: 'right-0 top-2 bottom-2 w-1.5 translate-x-1/2', cursor: 'ew-resize' },
    { edge: 'w', className: 'left-0 top-2 bottom-2 w-1.5 -translate-x-1/2', cursor: 'ew-resize' },
    { edge: 'ne', className: 'top-0 right-0 h-3 w-3 -translate-y-1/2 translate-x-1/2', cursor: 'nesw-resize' },
    { edge: 'nw', className: 'top-0 left-0 h-3 w-3 -translate-y-1/2 -translate-x-1/2', cursor: 'nwse-resize' },
    { edge: 'se', className: 'bottom-0 right-0 h-3 w-3 translate-y-1/2 translate-x-1/2', cursor: 'nwse-resize' },
    { edge: 'sw', className: 'bottom-0 left-0 h-3 w-3 translate-y-1/2 -translate-x-1/2', cursor: 'nesw-resize' },
];

const OVERLAY_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
    pagerank: { label: 'PageRank', icon: <TrendingUp className="w-3.5 h-3.5" /> },
    betweenness: { label: 'Betweenness Centrality', icon: <Activity className="w-3.5 h-3.5" /> },
    community: { label: 'Communities', icon: <Users className="w-3.5 h-3.5" /> },
    risk: { label: 'Risk Heatmap', icon: <ShieldAlert className="w-3.5 h-3.5" /> },
};

function parentBounds(el: HTMLElement | null): { width: number; height: number } {
    const parent = el?.offsetParent as HTMLElement | null;
    if (!parent) {
        return {
            width: typeof window !== 'undefined' ? window.innerWidth : 1200,
            height: typeof window !== 'undefined' ? window.innerHeight : 800,
        };
    }
    return { width: parent.clientWidth, height: parent.clientHeight };
}

function clampRect(rect: PanelRect, bounds: { width: number; height: number }): PanelRect {
    const width = Math.max(MIN_WIDTH, Math.min(rect.width, Math.max(MIN_WIDTH, bounds.width - 8)));
    const height = Math.max(MIN_HEIGHT, Math.min(rect.height, Math.max(MIN_HEIGHT, bounds.height - 8)));
    const maxLeft = Math.max(0, bounds.width - width);
    const maxTop = Math.max(0, bounds.height - height);
    return {
        width,
        height,
        left: Math.min(Math.max(0, rect.left), maxLeft),
        top: Math.min(Math.max(0, rect.top), maxTop),
    };
}

export const GraphOverlayLegend: React.FC<GraphOverlayLegendProps> = ({
    activeOverlay,
    legend,
    isComputing,
    onClear,
    className,
}) => {
    const [rect, setRect] = useState<PanelRect>({ ...OVERLAY_DEFAULT });
    const panelRef = useRef<HTMLDivElement>(null);
    const interaction = useRef<{
        mode: 'drag' | 'resize';
        edge?: ResizeEdge;
        startX: number;
        startY: number;
        origin: PanelRect;
    } | null>(null);

    const applyClamped = useCallback((next: PanelRect) => {
        setRect(clampRect(next, parentBounds(panelRef.current)));
    }, []);

    const resetPosition = useCallback(() => {
        applyClamped({ ...OVERLAY_DEFAULT });
    }, [applyClamped]);

    const applyClampedRef = useRef(applyClamped);
    useEffect(() => {
        applyClampedRef.current = applyClamped;
    }, [applyClamped]);

    const onPointerMove = useCallback((e: PointerEvent) => {
        const state = interaction.current;
        if (!state) return;
        const dx = e.clientX - state.startX;
        const dy = e.clientY - state.startY;
        const { origin } = state;
        const clamp = applyClampedRef.current;

        if (state.mode === 'drag') {
            clamp({
                ...origin,
                top: origin.top + dy,
                left: origin.left + dx,
            });
            return;
        }

        const edge = state.edge!;
        let { top, left, width, height } = origin;
        if (edge.includes('e')) width = origin.width + dx;
        if (edge.includes('w')) {
            width = origin.width - dx;
            left = origin.left + dx;
        }
        if (edge.includes('s')) height = origin.height + dy;
        if (edge.includes('n')) {
            height = origin.height - dy;
            top = origin.top + dy;
        }
        clamp({ top, left, width, height });
    }, []);

    const onPointerUpRef = useRef<(e: PointerEvent) => void>(() => undefined);

    const onPointerUp = useCallback((e: PointerEvent) => {
        if (!interaction.current) return;
        interaction.current = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUpRef.current);
        window.removeEventListener('pointercancel', onPointerUpRef.current);
        try {
            (e.target as HTMLElement | null)?.releasePointerCapture?.(e.pointerId);
        } catch {
            // Already released.
        }
    }, [onPointerMove]);

    useEffect(() => {
        onPointerUpRef.current = onPointerUp;
    }, [onPointerUp]);

    const beginInteraction = useCallback((
        e: React.PointerEvent,
        mode: 'drag' | 'resize',
        edge?: ResizeEdge,
    ) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        interaction.current = {
            mode,
            edge,
            startX: e.clientX,
            startY: e.clientY,
            origin: { ...rect },
        };
        document.body.style.userSelect = 'none';
        document.body.style.cursor = mode === 'drag'
            ? 'grabbing'
            : (RESIZE_HANDLES.find(h => h.edge === edge)?.cursor ?? 'default');
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
    }, [rect, onPointerMove, onPointerUp]);

    useEffect(() => () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, [onPointerMove, onPointerUp]);

    useEffect(() => {
        const parent = panelRef.current?.offsetParent as HTMLElement | null;
        if (!parent || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            setRect(prev => clampRect(prev, parentBounds(panelRef.current)));
        });
        ro.observe(parent);
        return () => ro.disconnect();
    }, []);

    if (!activeOverlay) return null;

    const overlayMeta = OVERLAY_LABELS[activeOverlay] || { label: activeOverlay, icon: null };

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-label={`${overlayMeta.label} overlay legend`}
            data-testid="graph-overlay-legend"
            data-tour="overlay-legend"
            className={clsx(
                workbenchSurface,
                'absolute z-30 flex flex-col overflow-hidden rounded-xl shadow-overlay',
                className,
            )}
            style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            }}
        >
            <div
                data-testid="graph-overlay-legend-drag-handle"
                onPointerDown={(e) => beginInteraction(e, 'drag')}
                className="px-3 py-2 flex items-center justify-between bg-brand-ink border-b-4 border-b-primary cursor-grab active:cursor-grabbing select-none shrink-0 touch-none"
            >
                <div className="flex items-center gap-1.5 min-w-0 text-white">
                    <GripHorizontal className="w-3.5 h-3.5 text-white/45 shrink-0" aria-hidden />
                    {overlayMeta.icon}
                    <div className="min-w-0">
                        <span className="block font-semibold text-xs leading-tight truncate">{overlayMeta.label}</span>
                        <span className="block text-[10px] text-white/65 truncate">Drag to move · resize from edges</span>
                    </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={resetPosition}
                        className="p-1 rounded text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Reset overlay legend position"
                        title="Reset position"
                        data-testid="graph-overlay-legend-reset"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={onClear}
                        className="p-1 rounded text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Clear overlay"
                        title="Clear overlay"
                        data-testid="graph-overlay-legend-close"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            <div className="p-3 flex-1 min-h-0 overflow-y-auto styled-scrollbar bg-card/40 dark:bg-card/60">
                {isComputing && (
                    <div className="text-[10px] text-muted-foreground mb-2 flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        Computing...
                    </div>
                )}

                <div className="space-y-1.5">
                    {legend.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px]">
                            <div
                                className="w-3 h-3 rounded-sm shrink-0"
                                style={{ backgroundColor: item.color }}
                            />
                            <span className="text-foreground/80">{item.label}</span>
                            {item.range && (
                                <span className="text-muted-foreground ml-auto tabular-nums">{item.range}</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {RESIZE_HANDLES.map(({ edge, className: handleClass, cursor }) => (
                <div
                    key={edge}
                    data-testid={`graph-overlay-legend-resize-${edge}`}
                    aria-hidden
                    onPointerDown={(e) => beginInteraction(e, 'resize', edge)}
                    className={`absolute z-50 touch-none ${handleClass}`}
                    style={{ cursor }}
                />
            ))}
        </div>
    );
};
